import { eur } from "@/lib/api";

const DOC_LABELS = { ostra: "FAKTÚRA", ponuka: "CENOVÁ PONUKA", zalohova: "ZÁLOHOVÁ FAKTÚRA" };
const PAYMENT_LABELS = { prevod: "Bankový prevod", hotovost: "Hotovosť", karta: "Platobná karta", dobierka: "Dobierka" };

export default function InvoicePrint({ invoice, settings, qr }) {
  if (!invoice) return null;
  const s = settings || {};
  const c = invoice.customer || {};
  const v = invoice.vehicle || {};
  const items = invoice.items || [];
  const rc = invoice.vat_mode === "RC";

  return (
    <div className="print-page mx-auto max-w-[800px] rounded-lg bg-white p-8 text-slate-900 shadow-xl" data-testid="invoice-print-area">
      <div className="flex items-start justify-between border-b-2 border-slate-900 pb-4">
        <div className="flex items-center gap-3">
          {s.logo && <img src={s.logo} alt="logo" className="h-16 w-auto" />}
          <div>
            <div className="text-xl font-extrabold uppercase" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>{s.name || "Váš servis"}</div>
            <div className="text-xs text-slate-600">{s.address}{s.psc || s.city ? `, ${s.psc || ""} ${s.city || ""}` : ""}</div>
            <div className="text-xs text-slate-600">IČO: {s.ico || "—"} · DIČ: {s.dic || "—"}{s.ic_dph ? ` · IČ DPH: ${s.ic_dph}` : ""}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-extrabold uppercase" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>{DOC_LABELS[invoice.doc_type]}</div>
          <div className="text-lg font-bold">č. {invoice.number}</div>
          <div className="text-xs text-slate-600">VS: {invoice.variable_symbol}</div>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-6 text-sm">
        <div>
          <div className="mb-1 text-xs font-bold uppercase text-slate-500">Odberateľ</div>
          <div className="font-semibold">{c.name || "—"}</div>
          <div>{c.address}</div>
          <div>{[c.psc, c.city].filter(Boolean).join(" ")}{c.country ? `, ${c.country}` : ""}</div>
          <div className="mt-1 text-xs">{c.ico && `IČO: ${c.ico}`} {c.dic && `· DIČ: ${c.dic}`}</div>
          {c.ic_dph && <div className="text-xs">IČ DPH: {c.ic_dph}</div>}
        </div>
        <div className="text-sm">
          <div className="flex justify-between"><span className="text-slate-500">Dátum vystavenia:</span><span>{invoice.issue_date}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Dátum splatnosti:</span><span>{invoice.due_date}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Forma úhrady:</span><span>{PAYMENT_LABELS[invoice.payment_type] || "—"}</span></div>
          {v.spz && <div className="flex justify-between"><span className="text-slate-500">Vozidlo:</span><span>{v.spz} · {v.brand} {v.model}</span></div>}
          {v.vin && <div className="flex justify-between"><span className="text-slate-500">VIN:</span><span className="font-mono text-xs">{v.vin}</span></div>}
        </div>
      </div>

      <table className="mt-6 w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-slate-300 text-left text-xs uppercase text-slate-500">
            <th className="py-2">Popis</th><th className="py-2 text-right">Množ.</th><th className="py-2 text-right">Cena/j.</th><th className="py-2 text-right">Zľava</th>
            {!rc && <th className="py-2 text-right">DPH%</th>}<th className="py-2 text-right">Spolu</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={i} className="border-b border-slate-100">
              <td className="py-2">{it.description}</td>
              <td className="py-2 text-right">{it.qty} {it.unit}</td>
              <td className="py-2 text-right">{eur(it.unit_price)}</td>
              <td className="py-2 text-right">{Number(it.discount || 0) ? `${Number(it.discount)}%` : "—"}</td>
              {!rc && <td className="py-2 text-right">{it.vat_rate}%</td>}
              <td className="py-2 text-right font-medium">{eur(Number(it.qty) * Number(it.unit_price || 0) * (1 - Number(it.discount || 0) / 100))}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-4 flex justify-between">
        <div className="space-y-3">
          {qr?.available && (
            <div className="text-center">
              <img src={qr.qr_png} alt="PAY by square" className="h-36 w-36" data-testid="invoice-qr-image" />
              <div className="mt-1 text-xs font-bold uppercase text-slate-600">PAY by square</div>
              <div className="text-[10px] text-slate-500">Naskenujte v mobilnej banke</div>
            </div>
          )}
          {s.iban && <div className="text-xs text-slate-600">IBAN: <span className="font-mono">{s.iban}</span></div>}
        </div>
        <div className="w-64 space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-slate-500">Základ dane</span><span>{eur(invoice.subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">DPH{rc ? " (prenesenie)" : ""}</span><span>{eur(invoice.vat_total)}</span></div>
          <div className="flex justify-between border-t-2 border-slate-900 pt-2 text-lg font-extrabold"><span>Spolu</span><span>{eur(invoice.total)}</span></div>
        </div>
      </div>

      {rc && <div className="mt-4 rounded bg-slate-100 p-2 text-xs text-slate-600">Prenesenie daňovej povinnosti podľa § 69 ods. 12 zákona o DPH.</div>}
      {s.invoice_note && <div className="mt-4 text-xs text-slate-500">{s.invoice_note}</div>}
    </div>
  );
}
